import Box from "@mui/material/Box";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Outlet} from "react-router-dom";


import RaildaysScrollToTop from "./RaildaysScrollToTop.js";

const LOCATION_REQUESTED_SESSION_KEY = "raildays.location.requested";
const LOCATION_CACHE_SESSION_KEY = "raildays.location.cache";
const LOCATION_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const readCachedLocation = () => {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.sessionStorage.getItem(LOCATION_CACHE_SESSION_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		const lat = Number(parsed?.lat);
		const lng = Number(parsed?.lng);
		const ts = Number(parsed?.ts);
		if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
		if (!Number.isNaN(ts) && ((Date.now() - ts) > LOCATION_CACHE_MAX_AGE_MS)) return null;
		return {
			lat,
			lng,
			label: parsed?.label || "Nearby"
		};
	}
	catch {
		return null;
	}
};

const writeCachedLocation = location => {
	if (typeof window === "undefined") return;
	try {
		window.sessionStorage.setItem(
			LOCATION_CACHE_SESSION_KEY,
			JSON.stringify({
				lat: location?.lat,
				lng: location?.lng,
				label: location?.label || "Nearby",
				ts: Date.now()
			})
		);
	}
	catch {
		// Ignore storage restrictions.
	}
};

const RaildaysLayout = () => {
	const [targetLocation, setTargetLocation] = useState(() => readCachedLocation());
	const [locationLoading, setLocationLoading] = useState(false);

	const setSharedTargetLocation = useCallback(nextLocation => {
		setTargetLocation(prevLocation => {
			const resolvedLocation =
				(typeof nextLocation === "function") ?
					nextLocation(prevLocation) :
					nextLocation;
			if (resolvedLocation) writeCachedLocation(resolvedLocation);
			return resolvedLocation;
		});
	}, []);

	const requestCurrentLocation = useCallback(() => {
		if (!navigator.geolocation) return;
		if (locationLoading) return;

		setLocationLoading(true);
		navigator.geolocation.getCurrentPosition(
			position => {
				const nextLocation = {
					lat: position.coords.latitude,
					lng: position.coords.longitude,
					label: "Nearby"
				};
				setSharedTargetLocation(nextLocation);
				setLocationLoading(false);
			},
			() => setLocationLoading(false),
			{
				enableHighAccuracy: false,
				timeout: 12000,
				maximumAge: 300000
			}
		);
	}, [locationLoading, setSharedTargetLocation]);

	useEffect(() => {
		if (!navigator.geolocation) return;

		try {
			const alreadyRequested = window.sessionStorage.getItem(
				LOCATION_REQUESTED_SESSION_KEY
			);
			if (alreadyRequested === "1") return;
			window.sessionStorage.setItem(LOCATION_REQUESTED_SESSION_KEY, "1");
		}
		catch {
			// Ignore storage restrictions and continue with a one-off request.
		}

		requestCurrentLocation();
	}, [requestCurrentLocation]);

	const outletContext = useMemo(
		() => ({
			targetLocation,
			setTargetLocation: setSharedTargetLocation,
			locationLoading,
			setLocationLoading,
			requestCurrentLocation
		}),
		[
			targetLocation,
			locationLoading,
			setSharedTargetLocation,
			requestCurrentLocation
		]
	);

	return (
		<Box
			sx={{
				"--raildays-shell-offset": "86px",
				"--raildays-header-height": {xs: "64px", md: "90px"},
				"--raildays-content-top": {
					xs: "64px",
					md: "90px"
				},
				"--raildays-content-bottom": {xs: "24px", md: "36px", lg: "48px"},
				"minHeight": "100vh",
				"width": "100%",
				"marginTop": "calc(-1 * var(--raildays-shell-offset))",
				"display": "flex",
				"flexDirection": "column",
				"backgroundImage": "url('/assets/Raildays/backgrounds/bricks.jpg')",
				"backgroundRepeat": "repeat",
				"backgroundAttachment": "fixed"
			}}>
			<RaildaysScrollToTop />
			<Box
				component="main"
				sx={{
					flex: 1,
					pt: "var(--raildays-content-top)",
					pb: "var(--raildays-content-bottom)"
				}}>
				<Outlet context={outletContext} />
			</Box>
		</Box>
	);
};

export default RaildaysLayout;
